import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CatalogCategoriesResponse,
  CatalogCategoryId,
  CatalogProductResponse,
  CatalogProductsResponse,
} from '../models/catalog.models';

export interface CatalogProductsFilter {
  categoryId?: CatalogCategoryId;
  search?: string;
  divisionId?: number;
}

export interface CatalogRequestOptions {
  forceRefresh?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private baseUrl = environment.apiUrl + '/catalog';
  private categoriesCache = new Map<string, CatalogCategoriesResponse>();
  private productsCache = new Map<string, CatalogProductsResponse>();

  constructor(private http: HttpClient) {}

  getCategories(
    date: string,
    divisionId?: number,
    options: CatalogRequestOptions = {}
  ): Observable<CatalogCategoriesResponse> {
    const key = this.categoriesKey(date, divisionId);
    if (!options.forceRefresh) {
      const cached = this.categoriesCache.get(key);
      if (cached) return of(cached);
    } else {
      this.categoriesCache.delete(key);
    }

    let params = new HttpParams().set('date', date);
    if (divisionId != null) {
      params = params.set('division_id', divisionId.toString());
    }
    return this.http
      .get<CatalogCategoriesResponse>(`${this.baseUrl}/categories`, { params })
      .pipe(tap((response) => this.categoriesCache.set(key, response)));
  }

  getProducts(
    date: string,
    filters: CatalogProductsFilter = {},
    options: CatalogRequestOptions = {}
  ): Observable<CatalogProductsResponse> {
    const normalizedSearch = this.normalizeSearch(filters.search);
    const cacheable = !filters.search || !!normalizedSearch;
    const key = this.productsKey(date, filters.categoryId, filters.divisionId, normalizedSearch);

    if (cacheable && !options.forceRefresh) {
      const cached = this.productsCache.get(key);
      if (cached) return of(cached);
    } else if (options.forceRefresh) {
      this.productsCache.delete(key);
    }

    let params = new HttpParams().set('date', date);
    if (filters.categoryId != null) {
      params = params.set('category_id', filters.categoryId.toString());
    }
    if (normalizedSearch) {
      params = params.set('search', normalizedSearch);
    }
    if (filters.divisionId != null) {
      params = params.set('division_id', filters.divisionId.toString());
    }
    return this.http
      .get<CatalogProductsResponse>(`${this.baseUrl}/products`, { params })
      .pipe(tap((response) => {
        if (cacheable) this.productsCache.set(key, response);
      }));
  }

  getProduct(id: number): Observable<CatalogProductResponse> {
    return this.http.get<CatalogProductResponse>(`${this.baseUrl}/products/${id}`);
  }

  clearCache(): void {
    this.categoriesCache.clear();
    this.productsCache.clear();
  }

  private normalizeSearch(search?: string): string | undefined {
    const value = search?.trim();
    return value && value.length >= 2 ? value : undefined;
  }

  private categoriesKey(date: string, divisionId?: number): string {
    return [date, divisionId ?? 'all'].join('|');
  }

  private productsKey(date: string, categoryId?: CatalogCategoryId, divisionId?: number, search?: string): string {
    return [
      date,
      divisionId ?? 'all',
      categoryId ?? 'all',
      search?.toLowerCase() ?? ''
    ].join('|');
  }
}
